import { useState, useEffect, useCallback } from "react";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogClose,
} from "@/components/ui/dialog";
import { GuidedInput } from "@/components/ui/guided-input";
import { Plus, Mail, MessageCircle, Clock, Trash2, Archive, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { DestructiveConfirm, RestoreConfirm } from "@/components/dialogs";
import { LoadingState, EmptyState } from "@/components/states";
import { cn } from "@/lib/utils";

const ALERT_KEYS = [
  { key: "daily", label: "Daily report" },
  { key: "weekly", label: "Weekly report" },
  { key: "approvals", label: "Approval alerts" },
  { key: "overdue", label: "Overdue alerts" },
  { key: "critical", label: "Critical issue alerts" },
  { key: "performance", label: "Performance alerts" },
];

export default function ReportRecipients() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("active"); // active | archived
  const [form, setForm] = useState({ name: "", role: "", email: "", whatsapp: "", delivery_time: "8:00 AM" });
  const [confirm, setConfirm] = useState(null); // {type:'delete'|'restore', r}

  const load = useCallback(async (status) => {
    setLoading(true);
    setList(await apiFetch(`/report-recipients?status=${status}`));
    setLoading(false);
  }, []);
  useEffect(() => { load(tab).catch((e) => { toast.error(e.message); setLoading(false); }); }, [tab, load]);

  const add = async () => {
    if (!form.name.trim()) return;
    try { await apiFetch("/report-recipients", { method: "POST", body: JSON.stringify(form) }); toast.success("Recipient added"); setForm({ name: "", role: "", email: "", whatsapp: "", delivery_time: "8:00 AM" }); await load(tab); }
    catch (e) { toast.error(e.message); }
  };
  const toggle = async (r, key, value) => {
    try { await apiFetch(`/report-recipients/${r.id}`, { method: "PUT", body: JSON.stringify({ ...r, [key]: value }) }); }
    catch (e) { toast.error(e.message); }
  };
  const archive = async (r) => {
    try { await apiFetch(`/report-recipients/${r.id}/archive`, { method: "PUT" }); toast.success("Moved to archive"); await load(tab); }
    catch (e) { toast.error(e.message); }
  };
  const doRestore = async () => {
    const r = confirm.r; setConfirm(null);
    try { await apiFetch(`/report-recipients/${r.id}/restore`, { method: "PUT" }); toast.success("Recipient restored"); await load(tab); }
    catch (e) { toast.error(e.message); }
  };
  const doDelete = async () => {
    const r = confirm.r; setConfirm(null);
    try { await apiFetch(`/report-recipients/${r.id}`, { method: "DELETE" }); toast.success("Recipient deleted"); await load(tab); }
    catch (e) { toast.error(e.message); }
  };

  const slug = (n) => n.toLowerCase().replace(/\s+/g, "-");

  return (
    <div className="animate-fade-up">
      <PageHeader eyebrow="Settings" title="Owner & Report Recipients"
        subtitle="A separate, configurable list of people who receive management reports and alerts."
        actions={
          <Dialog>
            <DialogTrigger asChild><Button className="gap-2" data-testid="add-recipient"><Plus className="h-4 w-4" /> Add Recipient</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-heading text-2xl">Add a report recipient</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <GuidedInput label="Name" required dataTestId="recipient-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" helperText="Who will receive these reports." exampleText="Vijayalakshmi" />
                <GuidedInput label="Role" dataTestId="recipient-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="e.g. Owner" />
                <GuidedInput label="Email" dataTestId="recipient-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="name@nirvana.example" />
                <GuidedInput label="WhatsApp" dataTestId="recipient-whatsapp" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="+91 …" />
              </div>
              <DialogFooter>
                <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
                <DialogClose asChild><Button onClick={add} data-testid="save-recipient">Add Recipient</Button></DialogClose>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        } />

      <div className="mb-5 inline-flex rounded-lg border border-border p-0.5" data-testid="recipient-tabs">
        {[["active", "Active"], ["archived", "Archived"]].map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`recipient-tab-${v}`}
            className={cn("rounded-md px-3 py-1.5 text-sm", tab === v ? "bg-secondary text-foreground" : "text-muted-foreground")}>{l}</button>
        ))}
      </div>

      {loading ? (
        <LoadingState label="Loading recipients…" />
      ) : list.length === 0 ? (
        <Card className="shadow-soft">
          <EmptyState title={tab === "archived" ? "No archived recipients" : "No recipients yet"}
            message={tab === "archived" ? "Archived recipients you can restore will appear here." : "Add someone who should receive management reports."} />
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {list.map((r) => (
            <Card key={r.id} className="p-6 shadow-soft" data-testid={`recipient-${slug(r.name)}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-heading text-xl font-semibold text-foreground">{r.name}</h3>
                  <p className="text-sm text-muted-foreground">{r.role}</p>
                </div>
                <div className="flex items-center gap-1">
                  {tab === "active" ? (
                    <>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => archive(r)} title="Archive" data-testid={`archive-${r.id}`}><Archive className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => setConfirm({ type: "delete", r })} title="Delete" data-testid={`remove-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </>
                  ) : (
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-accent" onClick={() => setConfirm({ type: "restore", r })} title="Restore" data-testid={`restore-${r.id}`}><RotateCcw className="h-4 w-4" /></Button>
                  )}
                </div>
              </div>
              <div className="mt-4 space-y-2 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground"><Mail className="h-4 w-4 text-accent" /> {r.email || "—"}</div>
                <div className="flex items-center gap-2 text-muted-foreground"><MessageCircle className="h-4 w-4 text-accent" /> {r.whatsapp || "—"}</div>
                <div className="flex items-center gap-2 text-muted-foreground"><Clock className="h-4 w-4 text-accent" /> Preferred delivery: {r.delivery_time}</div>
              </div>
              {tab === "active" && <>
                <div className="lotus-divider my-5" />
                <div className="space-y-1">
                  {ALERT_KEYS.map((a) => (
                    <div key={a.key} className="flex items-center justify-between py-1.5">
                      <span className="text-sm text-foreground">{a.label}</span>
                      <Switch defaultChecked={!!r[a.key]} onCheckedChange={(v) => toggle(r, a.key, v)} data-testid={`${slug(r.name)}-${a.key}`} />
                    </div>
                  ))}
                </div>
              </>}
            </Card>
          ))}
        </div>
      )}

      <DestructiveConfirm open={confirm?.type === "delete"} onOpenChange={(o) => !o && setConfirm(null)}
        title="Delete this recipient?" description={`“${confirm?.r?.name}” will be removed from the active list. You can still find and restore it from Deleted records, and audit history is kept.`}
        onConfirm={doDelete} />
      <RestoreConfirm open={confirm?.type === "restore"} onOpenChange={(o) => !o && setConfirm(null)}
        title="Restore this recipient?" description={`“${confirm?.r?.name}” will be moved back to the active list.`}
        onConfirm={doRestore} />
    </div>
  );
}
